import { Link } from '@tanstack/react-router'
import { StatusBadge, type Status } from './StatusBadge'
import { ProcessBadge } from './ProcessBadge'

export interface IdeaAuthor {
  full_name: string
  position?: string | null
  business_unit_name?: string | null
  department_name?: string | null
  employee_number?: string | null
}

export interface IdeaSummary {
  id: string
  code: string
  title: string
  description: string
  status: Status
  ai_status: string
  version: number
  author?: IdeaAuthor | null
  target_department_name?: string | null
  process_tags?: string[]
  created_at: string
  updated_at: string
}

export function IdeaCard({ idea }: { idea: IdeaSummary }) {
  const created = new Date(idea.created_at).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
  return (
    <Link
      to="/ideas/$id"
      params={{ id: idea.id }}
      className="block bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-4 hover:border-amber-400 dark:hover:border-amber-500 transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-mono text-zinc-500 dark:text-zinc-400">{idea.code}</p>
          <h3 className="mt-1 font-semibold text-zinc-900 dark:text-zinc-100 truncate">
            {idea.title}
          </h3>
          {idea.author && (
            <p className="mt-1 text-xs text-zinc-500 truncate">
              👤 {idea.author.full_name}
            </p>
          )}
          {idea.author?.position && (
            <p className="text-xs text-zinc-500 truncate">{idea.author.position}</p>
          )}
          {idea.target_department_name && (
            <p className="text-xs text-zinc-500 truncate">
              🏭 {idea.target_department_name}
            </p>
          )}
          {idea.process_tags && idea.process_tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {idea.process_tags.map((code) => (
                <ProcessBadge key={code} code={code} />
              ))}
            </div>
          )}
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2 break-words [overflow-wrap:anywhere]">
            {idea.description}
          </p>
        </div>
        <StatusBadge status={idea.status} />
      </div>
      <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">{created}</p>
    </Link>
  )
}
